// components
import { Dialog } from 'components/shared';
import { Typography } from '@mui/material';
import {
  GridCompact4,
  GridCompact8,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import dayjs from 'dayjs';
import {
  StyledBoxFlexColumn,
  StyledBoxFullWidth
} from 'components/shared/Box/Box.styles';

// constants
import {
  COST_CENTER_INFO_ADDRESS_KEYS,
  COST_CENTER_INFO_BASE_KEYS,
  COST_CENTER_INFO_REMARKS_KEYS
} from './CostCenterInfoDialog.consts';

// external libraries
import { useTranslation } from 'react-i18next';

// hooks
import useGetPlanUnitDetails from 'components/roster-portal/plan-units/hooks/useGetPlanUnitDetails';

// styles
import {
  StyledTopInfoContainer,
  StyledValueTypography
} from './CostCenterInfoDialog.styles';

// types
import { CostCenterInfoDialogProps } from './CostCenterInfoDialog.types';

// utils
import { makeItems } from './CostCenterInfoDialog.utils';
import { StyledCustomDialogPaper } from './form/QualificationSection.styles';

const CostCenterInfoDialog = ({
  isOpen,
  onClose,
  selectedRow
}: CostCenterInfoDialogProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch.costCenterInfoDialog'
  });

  const { data: costCenterData = [], isFetching } = useGetPlanUnitDetails(
    selectedRow?.costCenterId,
    dayjs(selectedRow?.date).toDate()
  );

  const handleClose = () => {
    onClose?.();
  };

  if (!selectedRow || isFetching) return null;

  const renderInfoItem = (label, value, index) => (
    <GridContainer key={index} spacing={2} direction="row">
      <GridCompact4>
        <Typography variant="body1">{label}</Typography>
      </GridCompact4>
      <GridCompact8>
        <StyledValueTypography variant="body1">
          {value || '-'}
        </StyledValueTypography>
      </GridCompact8>
    </GridContainer>
  );

  const costCenterInfoBase = makeItems(
    COST_CENTER_INFO_BASE_KEYS,
    costCenterData,
    t
  );
  const costCenterInfoAddress = makeItems(
    COST_CENTER_INFO_ADDRESS_KEYS,
    costCenterData,
    t
  );
  const costCenterInfoRemarks = makeItems(
    COST_CENTER_INFO_REMARKS_KEYS,
    costCenterData,
    t
  );

  return (
    <Dialog
      open={isOpen}
      title={`${selectedRow.costCenterName} · ${dayjs(selectedRow.date).format(
        'DD-MM-YYYY'
      )} · ${selectedRow.requestId}`}
      handleClose={handleClose}
      slots={{
        paper: StyledCustomDialogPaper
      }}
    >
      <StyledTopInfoContainer>
        <StyledBoxFullWidth>
          {costCenterInfoBase.map(({ label, value }, index) =>
            renderInfoItem(label, value, index)
          )}
        </StyledBoxFullWidth>
        <StyledBoxFullWidth>
          {costCenterInfoAddress.map(({ label, value }, index) =>
            renderInfoItem(label, value, index)
          )}
        </StyledBoxFullWidth>
      </StyledTopInfoContainer>

      <StyledBoxFlexColumn>
        {costCenterInfoRemarks.map(({ label, value }, index) =>
          renderInfoItem(label, value, index)
        )}
      </StyledBoxFlexColumn>
    </Dialog>
  );
};

export default CostCenterInfoDialog;
